import {
  CFormCheck,
  CFormSelect,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import DeleteIcon from "../../components/icons/DeleteIcon";
import EditIcon from "../../components/icons/EditIcon";
import PromptModal from "../../components/modals/PromptModal";
import { DELETE_ACTIONS } from "../../config/constants";
import { colors } from "../../config/theme";
import {
  iconBtnStyle,
  STATUS_OPTIONS,
  tdStyle,
  thStyle,
} from "../../helpers/constants";
import { Chip, formatDateStringWithTime } from "../../helpers/general";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";

/* ─────────────── Small building blocks ─────────────── */

function StatusChip({ status }: { status: string }) {
  switch (status) {
    case "approved":
      return <Chip tone="success">✓ Approuvé</Chip>;
    case "draft":
      return <Chip tone="dark">Brouillon</Chip>;
    case "pending_review":
      return <Chip tone="warning">En attente</Chip>;
    case "rejected":
      return <Chip tone="danger">✕ Rejeté</Chip>;
    default:
      return <Chip>{status}</Chip>;
  }
}

/* ─────────────── Row ─────────────── */

function TableBodyRowContent({
  activity,
  deletedList,
}: {
  activity: any;
  deletedList: React.Dispatch<any>;
}) {
  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { toastSuccess, toastError } = useToast();
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();

  const deleteMutation = useMutation((id: string) => {
    return apiService.MakeDeleteRequest(`activities/${id}`, token);
  });

  const updateStatusMutation = useMutation((status: string) => {
    return apiService.MakePutRequest(
      `activities/${activity.id}/status`,
      { status },
      token,
    );
  });

  useEffect(() => {
    if (isChecked) {
      deletedList({ type: DELETE_ACTIONS.ADD, id: activity.id });
    } else {
      deletedList({ type: DELETE_ACTIONS.REMOVE, id: activity.id });
    }
  }, [isChecked, activity.id, deletedList]);

  const handleDelete = () => {
    deleteMutation.mutate(activity.id, {
      onSuccess: () => {
        toastSuccess("Activité supprimée avec succès");
        queryClient.invalidateQueries("getActivities");
      },
      onError: () => {
        toastError(general.fr.message.operationFailed);
      },
    });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value;
    if (!newStatus || newStatus === activity.status) return;

    updateStatusMutation.mutate(newStatus, {
      onSuccess: () => {
        toastSuccess("Statut mis à jour avec succès");
        queryClient.invalidateQueries("getActivities");
      },
      onError: () => {
        toastError(general.fr.message.operationFailed);
      },
    });
  };

  const title = activity.title || "Sans titre";
  // Adjust these field names if your activity model stores its image elsewhere
  const image =
    activity.galleries?.[0]?.url ??
    `https://ui-avatars.com/api/?name=${encodeURIComponent(title)}`;

  return (
    <CTableRow
      style={{
        backgroundColor: isChecked ? "rgba(240,129,63,0.06)" : undefined,
        boxShadow: isChecked ? `inset 3px 0 0 ${colors.primary}` : undefined,
      }}
    >
      {/* Checkbox */}
      <CTableDataCell style={{ ...tdStyle, width: 44 }}>
        <CFormCheck
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
        />
      </CTableDataCell>

      {/* Image + title */}
      <CTableDataCell style={tdStyle}>
        <div
          className="d-flex align-items-center gap-2"
          style={{ minWidth: 220 }}
        >
          <img
            src={image}
            alt=""
            width={44}
            height={44}
            className="flex-shrink-0"
            style={{ borderRadius: 10, objectFit: "cover" }}
          />
          <Link
            to={`/activities/update/${activity.id}`}
            className="fw-semibold text-truncate"
            style={{ maxWidth: 260 }}
            title={title}
          >
            {title}
          </Link>
        </div>
      </CTableDataCell>

      {/* Created at */}
      <CTableDataCell style={tdStyle}>
        <span style={{ whiteSpace: "nowrap" }}>
          {formatDateStringWithTime(activity.createdAt)}
        </span>
      </CTableDataCell>

      {/* Place */}
      <CTableDataCell style={tdStyle}>
        {activity.place?.name ? (
          <span className="text-capitalize">{activity.place.name}</span>
        ) : (
          <span className="text-muted">Sans établissement</span>
        )}
      </CTableDataCell>

      {/* Status */}
      <CTableDataCell style={tdStyle}>
        <StatusChip status={activity.status} />
      </CTableDataCell>

      {/* Translations */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex flex-wrap gap-1">
          {activity.translations?.length > 0 ? (
            activity.translations.map((translation: any) => (
              <Chip key={translation.lang}>
                {String(translation.lang).toUpperCase()}
              </Chip>
            ))
          ) : (
            <span className="text-muted">—</span>
          )}
        </div>
      </CTableDataCell>

      {/* Order */}
      <CTableDataCell style={tdStyle}>
        {activity.order ? (
          <span className="fw-semibold">{activity.order}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </CTableDataCell>

      {/* Actions */}
      <CTableDataCell style={tdStyle}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          {isAdmin &&
            (updateStatusMutation.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              <CFormSelect
                size="sm"
                className="rounded-pill"
                style={{ width: 125 }}
                defaultValue={activity.status}
                onChange={handleStatusChange}
                disabled={deleteMutation.isLoading}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </CFormSelect>
            ))}

          <Link
            to={`/activities/update/${activity.id}`}
            title="Modifier"
            aria-label="Modifier"
            className="d-inline-flex align-items-center justify-content-center rounded-circle"
            style={iconBtnStyle}
          >
            <EditIcon width="16" height="16" fill={colors.secondary} />
          </Link>

          <button
            type="button"
            title="Supprimer"
            aria-label="Supprimer"
            onClick={() => setShowModal(true)}
            disabled={deleteMutation.isLoading}
            className="d-inline-flex align-items-center justify-content-center border-0 rounded-circle"
            style={{ ...iconBtnStyle, backgroundColor: "rgba(220,53,69,0.10)" }}
          >
            {deleteMutation.isLoading ? (
              <CSpinner size="sm" />
            ) : (
              <DeleteIcon width="18" height="18" fill={colors.primary} />
            )}
          </button>
        </div>
      </CTableDataCell>

      <PromptModal
        open={showModal}
        onAccept={handleDelete}
        onClose={() => setShowModal(false)}
      />
    </CTableRow>
  );
}

/* ─────────────── Table ─────────────── */

export default function ActivitiesTable({
  activities,
  onDeleteSelect,
}: {
  activities: any[];
  onDeleteSelect: React.Dispatch<any>;
}) {
  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            <CTableHeaderCell style={{ ...thStyle, width: 44 }} />
            <CTableHeaderCell scope="col" style={thStyle}>
              Titre
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Date de création
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Établissement
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Statut
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Traductions
            </CTableHeaderCell>
            <CTableHeaderCell scope="col" style={thStyle}>
              Ordre
            </CTableHeaderCell>
            <CTableHeaderCell
              scope="col"
              style={{ ...thStyle, textAlign: "right" }}
            >
              Actions
            </CTableHeaderCell>
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {activities?.map((activity) => (
            <TableBodyRowContent
              key={activity.id}
              activity={activity}
              deletedList={onDeleteSelect}
            />
          ))}
        </CTableBody>
      </CTable>
    </div>
  );
}
